import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileText, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReadinessBadge } from "@/components/Badges";
import { RecommendationView } from "@/components/RecommendationView";
import { EvidenceTag } from "@/components/onboarding/KitBuilder";
import { BrandLockup } from "@/pages/WelcomePage";
import { PageHead } from "@/pages/HistoryPage";
import { fmtDate } from "@/pages/Dashboard";

const areaLabel = (a) => (a === "lips" ? "Lip blush" : "Brows");

export function ReportsPage() {
  const { data = [], isLoading, isError } = useQuery({ queryKey: ["consultations"], queryFn: async () => (await api.get("/consultations")).data });
  const reports = data.filter((c) => c.readiness);
  return (
    <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10 lg:py-14">
      <PageHead eyebrow="Your records" title="Consultation reports" sub="A branded summary of every consultation that reached a recommendation. Open one to review it or print it for your client records." />
      <div className="mt-10 space-y-3" data-testid="reports-list">
        {isLoading && <p className="text-sm text-[#A88B7D]">Loading your reports…</p>}
        {isError && <p role="alert" className="rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">We couldn't load your reports. Please try again.</p>}
        {reports.map((c) => (
          <Link key={c.id} to={`/reports/${c.id}`} data-testid={`report-item-${c.id}`}
            className="group flex items-center gap-4 rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7] px-6 py-5 transition-colors hover:border-[#D99B90]">
            <FileText size={20} strokeWidth={1.5} className="shrink-0 text-[#C5A059]" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-xl font-semibold text-[#2C1810] group-hover:text-[#9C6218]">{c.title}</p>
              <p className="mt-0.5 text-xs text-[#A88B7D]">{areaLabel(c.area)} · {fmtDate(c.updated_at)}</p>
            </div>
            <span className="hidden sm:block"><ReadinessBadge status={c.readiness} /></span>
          </Link>
        ))}
        {!isLoading && !isError && !reports.length && (
          <div className="rounded-2xl bg-[#F6F1EA] p-6 text-sm text-[#6E5549]" data-testid="reports-empty">
            No reports yet. A report appears here once a consultation reaches a recommendation.{" "}
            <Link to="/consult/new" className="font-semibold text-[#9C6218] underline">Start a consultation</Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value, testId }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A88B7D]">{label}</dt>
      <dd className="mt-1 text-sm text-[#2C1810]" data-testid={testId}>{value || "—"}</dd>
    </div>
  );
}

export function ReportPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const cq = useQuery({ queryKey: ["consultation", id], queryFn: async () => (await api.get(`/consultations/${id}`)).data });
  const kq = useQuery({ queryKey: ["kit"], queryFn: async () => (await api.get("/kit")).data });

  if (cq.isLoading) return <p className="p-10 text-sm text-[#A88B7D]">Loading report…</p>;
  if (cq.isError) return <p role="alert" className="m-10 rounded-xl bg-[#F9ECEC] px-4 py-3 text-sm text-[#8C2A2A]">This report couldn't be loaded.</p>;

  const { consultation: c, messages = [] } = cq.data;
  const recMsg = [...messages].reverse().find((m) => m.recommendation);
  const kit = (kq.data || []).filter((k) => (c.kit_item_ids ?? (kq.data || []).map((x) => x.id)).includes(k.id));
  const p = user.profile || {};

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-10 lg:py-12 print:max-w-none print:p-0" data-testid="report-page">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to="/reports" data-testid="report-back-link" className="inline-flex min-h-[44px] items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#6E5549] hover:text-[#2C1810]"><ArrowLeft size={14} /> All reports</Link>
        <div className="flex gap-2">
          <Link to={`/consult/${id}`} data-testid="report-open-consult" className="inline-flex min-h-[44px] items-center rounded-full border border-[#E6DBCF] px-5 text-xs font-semibold text-[#2C1810] hover:bg-[#F6F1EA]">Open consultation</Link>
          <button onClick={() => window.print()} data-testid="report-print-btn" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#2C1810] px-5 text-xs font-semibold text-[#FDFBF7] hover:bg-[#3D2317]"><Printer size={14} /> Print / Save PDF</button>
        </div>
      </div>

      <article className="mt-6 rounded-3xl border border-[#E6DBCF] bg-white p-4 sm:p-10 print:mt-0 print:rounded-none print:border-0 print:p-0">
        <header className="flex flex-col gap-5 border-b border-[#E6DBCF] pb-6 sm:flex-row sm:items-end sm:justify-between">
          <BrandLockup />
          <div className="sm:text-right">
            <p className="font-mono-ig text-[11px] uppercase tracking-[0.3em] text-[#9C6218]">Consultation report</p>
            <p className="mt-1 text-xs text-[#6E5549]">Generated {fmtDate(new Date().toISOString())}</p>
          </div>
        </header>

        <h1 className="mt-8 font-serif text-3xl font-semibold text-[#2C1810] sm:text-4xl" data-testid="report-title">{c.title}</h1>
        <dl className="mt-6 grid gap-5 sm:grid-cols-4">
          <Meta label="Procedure area" value={areaLabel(c.area)} testId="report-area" />
          <Meta label="Started" value={fmtDate(c.created_at)} />
          <Meta label="Last updated" value={fmtDate(c.updated_at)} />
          <Meta label="Artist" value={[p.preferred_name, p.studio_name].filter(Boolean).join(" · ")} testId="report-artist" />
        </dl>

        <section className="mt-8">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6E5549]">Pigments available for this case</h2>
          <ul className="mt-3 flex flex-wrap gap-2" data-testid="report-kit">
            {kit.map((k) => (
              <li key={k.id} className="flex items-center gap-2 rounded-full border border-[#E6DBCF] bg-[#FDFBF7] py-1 pl-3 pr-1 text-xs text-[#2C1810]">
                {k.brand} · {k.shade || k.line} <EvidenceTag status={k.pigment?.verification_status} userAdded={!k.pigment_id} />
              </li>
            ))}
            {!kit.length && <li className="text-xs italic text-[#A88B7D]">No saved kit pigments were selected.</li>}
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6E5549]">Recommendation</h2>
          {recMsg ? <RecommendationView rec={recMsg.recommendation} mid={`report-${recMsg.id}`} hideDisclosure /> : <p className="text-sm italic text-[#A88B7D]">No structured recommendation was found in this consultation.</p>}
        </section>

        <footer className="mt-10 border-t border-[#E6DBCF] pt-5 text-xs leading-relaxed text-[#6E5549]" data-testid="report-disclosure">
          Ink Genius by MooreBeautyLove provides educational decision support for trained professionals. It does not provide medical clearance, confirm pigment chemistry from photos, or guarantee healed results. The treating professional makes the final decision. Confirm every pigment against the current bottle label, lot and manufacturer documentation.
        </footer>
      </article>
    </div>
  );
}
